import { useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { cleanText } from '@/utils/sanitize';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { X, Save, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  CertificationExamQuestion,
  QuestionType,
  certificationService,
} from '@/services/certification.service';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select, SelectOption } from '@/components/ui/Select';
import { QuestionOptionsField } from '@/components/ui/QuestionOptionsField';
import { questionValidationSchema } from '@/validations/questionValidation';

// Extracted as a proper component so hooks (useState) are valid
function KeywordsTextarea({ control, error }: { control: any; error?: string }) {
  const [rawText, setRawText] = useState('');
  const initializedRef = useRef(false);

  const parsedKeywords = rawText
    .split(',')
    .map((k) => k.trim())
    .filter((k) => k.length > 0);

  return (
    <Controller
      name="answer_keywords"
      control={control}
      render={({ field }) => {
        // Initialize raw text from field value once
        if (!initializedRef.current && field.value?.length > 0) {
          initializedRef.current = true;
          // Use setTimeout to avoid setState during render
          setTimeout(() => setRawText(field.value.join(', ')), 0);
        }

        return (
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Answer Keywords
              <span className="text-red-500 ml-1">*</span>
            </label>
            <textarea
              rows={2}
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              onBlur={() => {
                const keywords = rawText
                  .split(',')
                  .map((k) => k.trim())
                  .filter((k) => k.length > 0);
                field.onChange(keywords);
              }}
              className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 resize-y ${
                error ? 'border-red-500 focus:ring-red-500' : 'border-gray-200'
              }`}
              placeholder="Enter keywords separated by commas (e.g., React, JavaScript, Framework)"
            />
            {error && (
              <p className="text-red-500 text-xs mt-1">{error}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              Separate multiple keywords with commas. Example: React, JavaScript, Framework
            </p>
            {parsedKeywords.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                <p className="text-xs font-medium text-gray-600 w-full">Parsed Keywords:</p>
                {parsedKeywords.map((keyword, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      }}
    />
  );
}

interface EditQuestionModalProps {
  examId: string;
  question: CertificationExamQuestion;
  allQuestions: CertificationExamQuestion[];
  onClose: () => void;
  onSuccess: () => void;
}

export default function EditQuestionModal({
  examId,
  question,
  allQuestions,
  onClose,
  onSuccess,
}: EditQuestionModalProps) {
  // React Hook Form with Yup validation
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'all', // Validate on change, blur, and submit
    resolver: yupResolver(questionValidationSchema) as any,
    defaultValues: {
      ...question,
      options: question.options ? [...question.options] : [],
      answer_keywords: question.answer_keywords || [],
    },
  });

  const questionType = watch('question_type');

  // Update questions mutation
  const updateQuestionsMutation = useMutation({
    mutationFn: (questions: CertificationExamQuestion[]) =>
      certificationService.updateQuestions(examId, questions),
    onSuccess: () => {
      toast.success('Question updated successfully!');
      onSuccess();
    },
    onError: (error: any) => {
      const errorData = error.response?.data;
      if (errorData?.meta_data?.errors && Array.isArray(errorData.meta_data.errors)) {
        errorData.meta_data.errors.forEach((err: string) => {
          toast.error(err, { duration: 5000 });
        });
      } else if (errorData?.error?.error_description) {
        toast.error(errorData.error.error_description);
      } else if (errorData?.error_description) {
        toast.error(errorData.error_description);
      } else {
        toast.error('Failed to update question');
      }
    },
  });

  // Form submit handler
  const onSubmit = (data: any) => {
    // Prepare the question data based on type
    const questionData = { ...data };

    // Sanitize free-text before save (VAPT May 2026, #12/#13)
    questionData.question_text = cleanText(questionData.question_text);
    if (questionData.explanation) questionData.explanation = cleanText(questionData.explanation);
    if (questionData.hint) questionData.hint = cleanText(questionData.hint);
    if (questionData.options) {
      questionData.options = questionData.options.map((o: any) => ({
        ...o,
        text: cleanText(o.text),
        explanation: o.explanation ? cleanText(o.explanation) : o.explanation,
      }));
    }
    if (questionData.answer_keywords) {
      questionData.answer_keywords = questionData.answer_keywords
        .map((k: string) => cleanText(k))
        .filter((k: string) => k.length > 0);
    }

    if (data.question_type === 'TEXT_ANSWER') {
      // For text questions, remove options and correct_answer, keep only answer_keywords
      delete questionData.options;
      delete questionData.correct_answer;
    } else {
      // For choice questions, remove text answer fields
      delete questionData.correct_answer;
      delete questionData.answer_keywords;
    }

    // Update the questions array with the edited question
    const updatedQuestions = allQuestions.map((q) =>
      q.id === questionData.id ? questionData : q
    );

    updateQuestionsMutation.mutate(updatedQuestions);
  };

  // Question type options
  const questionTypeOptions: SelectOption[] = [
    { label: 'Single Choice', value: 'SINGLE_CHOICE' },
    { label: 'Multiple Choice', value: 'MULTIPLE_CHOICE' },
    { label: 'True/False', value: 'TRUE_FALSE' },
    { label: 'Text Answer', value: 'TEXT_ANSWER' },
  ];

  // Difficulty options
  const difficultyOptions: SelectOption[] = [
    { label: 'Easy', value: 'EASY' },
    { label: 'Medium', value: 'MEDIUM' },
    { label: 'Hard', value: 'HARD' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 common-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <Save className="w-7 h-7" />
            Edit Question
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Question Text */}
          <Textarea
            name="question_text"
            control={control}
            label="Question Text"
            placeholder="Enter your question here..."
            rows={3}
            required
            error={errors.question_text?.message}
          />

          {/* Question Type, Difficulty, and Points */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Select
              name="question_type"
              control={control}
              label="Question Type"
              options={questionTypeOptions}
              required
              error={errors.question_type?.message}
            />

            <Select
              name="difficulty_level"
              control={control}
              label="Difficulty"
              options={difficultyOptions}
              required
              error={errors.difficulty_level?.message}
            />

            <Input
              name="points"
              control={control}
              label="Points"
              type="number"
              min={1}
              required
              error={errors.points?.message}
            />
          </div>

          {/* Options or Text Answer */}
          {questionType !== 'TEXT_ANSWER' ? (
            <QuestionOptionsField
              name="options"
              control={control}
              label="Answer Options"
              questionType={questionType as QuestionType}
              minOptions={questionType === 'TRUE_FALSE' ? 2 : questionType === 'MULTIPLE_CHOICE' ? 3 : 2}
              required
              error={errors.options?.message}
            />
          ) : (
            <>
            <KeywordsTextarea
              control={control}
              error={errors.answer_keywords?.message}
            />

            {/* Case Sensitive Toggle */}
            <Controller
              name="case_sensitive"
              control={control}
              render={({ field }) => (
                <label className="flex items-center gap-3 mt-3 cursor-pointer select-none">
                  <div
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      field.value ? 'bg-purple-600' : 'bg-gray-300'
                    }`}
                    onClick={() => field.onChange(!field.value)}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        field.value ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    Case Sensitive
                  </span>
                  <span className="text-xs text-gray-500">
                    {field.value ? '(Exact case match required)' : '(Case insensitive matching)'}
                  </span>
                </label>
              )}
            />
            </>
          )}

          {/* Optional Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
            {/* <Input
              name="topic"
              control={control}
              label="Topic (Optional)"
              placeholder="e.g., React Hooks"
              error={errors.topic?.message}
            /> */}

            <Input
              name="bloom_level"
              control={control}
              label="Bloom Level (Optional)"
              placeholder="e.g., Understanding"
              error={errors.bloom_level?.message}
            />
          </div>

          <Textarea
            name="explanation"
            control={control}
            label="Explanation (Optional)"
            placeholder="Explain why this is the correct answer..."
            rows={2}
            error={errors.explanation?.message}
          />

          <Input
            name="hint"
            control={control}
            label="Hint (Optional)"
            placeholder="Provide a hint for learners..."
            error={errors.hint?.message}
          />
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit(onSubmit)}
            disabled={updateQuestionsMutation.isPending}
            className="px-6 py-2.5 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {updateQuestionsMutation.isPending ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
